import React from 'react';
import { cn } from '../lib/utils';
import { Clock, Users, CreditCard, AlertCircle } from 'lucide-react';

/**
 * CompactTableCard - Carte de table ultra-compacte pour affichage en grille dense
 * 
 * Objectif: Afficher un maximum de tables sans scroll
 * - Statut représenté uniquement par la couleur (pas de texte)
 * - Informations minimales visibles
 * - Détails complets au clic
 */

// Configuration couleurs par statut
const STATUS_COLORS = {
  available: {
    bg: 'bg-emerald-500',
    hover: 'hover:bg-emerald-600',
    ring: 'ring-emerald-300',
    text: 'text-white'
  },
  libre: {
    bg: 'bg-emerald-500',
    hover: 'hover:bg-emerald-600',
    ring: 'ring-emerald-300',
    text: 'text-white'
  },
  free: {
    bg: 'bg-emerald-500',
    hover: 'hover:bg-emerald-600',
    ring: 'ring-emerald-300',
    text: 'text-white'
  },
  brouillon: {
    bg: 'bg-amber-400',
    hover: 'hover:bg-amber-500',
    ring: 'ring-amber-300',
    text: 'text-amber-900'
  },
  occupee: {
    bg: 'bg-orange-500',
    hover: 'hover:bg-orange-600',
    ring: 'ring-orange-300',
    text: 'text-white'
  },
  occupied: {
    bg: 'bg-orange-500',
    hover: 'hover:bg-orange-600',
    ring: 'ring-orange-300',
    text: 'text-white'
  },
  sent_to_kitchen: {
    bg: 'bg-blue-500',
    hover: 'hover:bg-blue-600',
    ring: 'ring-blue-300',
    text: 'text-white'
  },
  en_preparation: {
    bg: 'bg-blue-500',
    hover: 'hover:bg-blue-600',
    ring: 'ring-blue-300',
    text: 'text-white'
  },
  ready: {
    bg: 'bg-purple-500',
    hover: 'hover:bg-purple-600',
    ring: 'ring-purple-300',
    text: 'text-white'
  },
  prete: {
    bg: 'bg-purple-500',
    hover: 'hover:bg-purple-600',
    ring: 'ring-purple-300',
    text: 'text-white'
  },
  paid: {
    bg: 'bg-gray-400',
    hover: 'hover:bg-gray-500',
    ring: 'ring-gray-300',
    text: 'text-white'
  },
  payee: {
    bg: 'bg-gray-400',
    hover: 'hover:bg-gray-500',
    ring: 'ring-gray-300',
    text: 'text-white'
  },
  en_attente_paiement: {
    bg: 'bg-red-500',
    hover: 'hover:bg-red-600',
    ring: 'ring-red-300',
    text: 'text-white'
  },
  en_cours_de_paiement: {
    bg: 'bg-indigo-500',
    hover: 'hover:bg-indigo-600',
    ring: 'ring-indigo-300',
    text: 'text-white'
  },
  a_debarrasser: {
    bg: 'bg-yellow-400',
    hover: 'hover:bg-yellow-500',
    ring: 'ring-yellow-300',
    text: 'text-yellow-900'
  },
  reservee: {
    bg: 'bg-violet-500',
    hover: 'hover:bg-violet-600',
    ring: 'ring-violet-300',
    text: 'text-white'
  }
};

// Format time elapsed (compact)
const formatTime = (timestamp) => {
  if (!timestamp) return null;
  const diff = Date.now() - new Date(timestamp).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 60) return `${minutes}'`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours}h${mins > 0 ? mins : ''}`;
};

// Format price (compact)
const formatPrice = (price) => {
  if (!price) return null;
  if (price >= 1000) {
    return `${Math.round(price / 1000)}k`;
  }
  return `${price}`;
};

const CompactTableCard = ({
  table,
  onClick,
  disabled = false
}) => {
  const status = table?.status || 'libre';
  const colors = STATUS_COLORS[status] || STATUS_COLORS.libre;
  
  // Calculate values
  const hasItems = (table?.sent_items?.length > 0) || (table?.draft_items?.length > 0);
  const calculatedTotal = 
    (table?.sent_items?.reduce((sum, item) => sum + (item.line_total || 0), 0) || 0) +
    (table?.draft_items?.reduce((sum, item) => sum + (item.line_total || 0), 0) || 0);
  const total = (status !== 'libre' && status !== 'available' && status !== 'free' && hasItems) ? (calculatedTotal || table?.total || 0) : 0;
  
  const timeElapsed = (status !== 'libre' && status !== 'available' && status !== 'free') ? formatTime(table?.opened_at) : null;
  const covers = table?.covers || table?.guests || 0;
  const isOccupied = status !== 'libre' && status !== 'available' && status !== 'free';
  
  // Server/Waiter name (use first name or initials for compact display)
  const serverName = table?.server_name || table?.waiter_name || table?.assigned_server || '';
  const serverInitials = serverName ? serverName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : '';
  
  // Indicators for occupied tables
  const hasAlert = status === 'en_attente_paiement' || status === 'a_debarrasser';

  return (
    <button
      onClick={() => !disabled && onClick?.(table)}
      disabled={disabled}
      data-testid={`compact-table-${table?.number}`}
      className={cn(
        // Base
        'relative w-full aspect-square rounded-lg',
        'flex flex-col items-center justify-center',
        'transition-all duration-150 ease-out',
        'active:scale-95 touch-manipulation',
        'shadow-sm hover:shadow-md',
        // Colors
        colors.bg,
        colors.hover,
        colors.text,
        // Disabled
        disabled && 'opacity-50 cursor-not-allowed',
        // Alert pulse for urgent statuses
        hasAlert && 'animate-pulse'
      )}
    >
      {/* Table Number - Large & Centered */}
      <span className="font-black text-2xl sm:text-3xl leading-none">
        {table?.number || '?'}
      </span>
      
      {/* Compact Info Row (only for occupied) */}
      {isOccupied && (
        <div className="flex items-center gap-1.5 mt-1 text-[10px] sm:text-xs opacity-90">
          {/* Time */}
          {timeElapsed && (
            <span className="flex items-center gap-0.5">
              <Clock className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
              {timeElapsed}
            </span>
          )}
          {/* Covers */}
          {covers > 0 && (
            <span className="flex items-center gap-0.5">
              <Users className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
              {covers}
            </span>
          )}
        </div>
      )}
      
      {/* Server initials badge (top left corner) */}
      {isOccupied && serverInitials && (
        <div className="absolute top-0.5 left-0.5 sm:top-1 sm:left-1 bg-black/30 rounded px-1 py-0.5 text-[8px] sm:text-[9px] font-bold uppercase">
          {serverInitials}
        </div>
      )}
      
      {/* Total badge (bottom right corner) */}
      {isOccupied && total > 0 && (
        <div className="absolute bottom-0.5 right-0.5 sm:bottom-1 sm:right-1 bg-black/30 rounded px-1 py-0.5 text-[9px] sm:text-[10px] font-bold">
          {formatPrice(total)}F
        </div>
      )}
      
      {/* Alert indicator (top right) */}
      {hasAlert && (
        <div className="absolute -top-1 -right-1 w-3 h-3 sm:w-4 sm:h-4 bg-white rounded-full flex items-center justify-center">
          <AlertCircle className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-red-500" />
        </div>
      )}
    </button>
  );
};

export default CompactTableCard;
